import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Upload, Camera, Edit3, Save, X, Eye, MapPin, Clock } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';
import { formatDistanceToNow } from 'date-fns';

interface ProofEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  visitId: number;
  onSuccess: () => void;
}

interface VisitDetails {
  id: number;
  storeId: number;
  operatorId: number;
  cycleId: number;
  signatureUrl?: string;
  latitude?: string;
  longitude?: string;
  completionStatus: string;
  submissionSource: string;
  notes?: string;
  discrepancyReason?: string;
  timestamp: string;
  lastEditedBy?: number;
  lastEditedAt?: string;
}

interface PostingEvent {
  eventId: number;
  taskId: number;
  postedUnits: number;
  timestamp: string;
  notes?: string;
  signedOffBy?: number;
  signedOffAt?: string;
  signOffType?: string;
  signOffNotes?: string;
}

interface EvidenceAsset {
  id: number;
  eventId: number;
  publicId: string;
  secureUrl: string;
  assetUrl?: string;
  assetType: string;
  meta?: any;
  createdAt: string;
}

export function ProofEditModal({ isOpen, onClose, visitId, onSuccess }: ProofEditModalProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [visitDetails, setVisitDetails] = useState<VisitDetails | null>(null);
  const [postingEvents, setPostingEvents] = useState<PostingEvent[]>([]);
  const [evidenceAssets, setEvidenceAssets] = useState<EvidenceAsset[]>([]);
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [mediaFormats, setMediaFormats] = useState<any[]>([]);
  const [formData, setFormData] = useState({
    notes: '',
    discrepancyReason: '',
    signatureUrl: ''
  });
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);

  useEffect(() => {
    if (isOpen && visitId) {
      fetchProofDetails();
    }
  }, [isOpen, visitId]);

  const fetchProofDetails = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/admin-signoff/proof-details/${visitId}`, {
        method: 'GET',
        credentials: 'omit',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Proof details API response:', data);
      
      if (data.success && data.visitDetails) {
        setVisitDetails(data.visitDetails);
        setPostingEvents(data.postingEvents || []);
        setEvidenceAssets(data.evidenceAssets || []);
        setCampaigns(data.campaigns || []);
        setMediaFormats(data.mediaFormats || []);
        
        setFormData({
          notes: data.visitDetails.notes || '',
          discrepancyReason: data.visitDetails.discrepancyReason || '',
          signatureUrl: data.visitDetails.signatureUrl || ''
        });
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Error fetching proof details:', error);
      toast({
        title: "Error",
        description: "Failed to load proof details",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    setSelectedFiles(files);
    
    // Generate preview URLs
    const previews = files.map(file => {
      const reader = new FileReader();
      return new Promise<string>((resolve) => {
        reader.onload = (e) => resolve(e.target?.result as string);
        reader.readAsDataURL(file);
      });
    });

    Promise.all(previews).then(setPreviewUrls);
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      // Upload new files if any
      const newEvidenceAssets = [];
      
      for (const file of selectedFiles) {
        const uploadFormData = new FormData();
        uploadFormData.append('file', file);
        uploadFormData.append('visitId', visitId.toString());
        uploadFormData.append('storeId', visitDetails?.storeId.toString() || '');
        uploadFormData.append('operatorId', visitDetails?.operatorId.toString() || '');
        uploadFormData.append('fileType', 'image');

        try {
          const response = await fetch('/api/admin-signoff/upload-admin-evidence', {
            method: 'POST',
            credentials: 'omit',
            body: uploadFormData
          });

          if (response.ok) {
            const result = await response.json();
            newEvidenceAssets.push({
              publicId: result.public_id,
              secureUrl: result.secure_url,
              assetType: 'image'
            });
          }
        } catch (uploadError) {
          console.error('File upload error:', uploadError);
        }
      }

      // Update proof record
      await apiRequest(`/api/admin-signoff/edit-proof/${visitId}`, 'PUT', {
        notes: formData.notes,
        discrepancyReason: formData.discrepancyReason,
        signatureUrl: formData.signatureUrl,
        newEvidenceAssets
      });

      toast({
        title: "Success",
        description: "Proof record updated successfully",
      });

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error updating proof:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update proof",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <div className="flex items-center justify-center p-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p>Loading proof details...</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit3 className="h-5 w-5" />
            Edit Proof of Posting
          </DialogTitle>
        </DialogHeader>

        {visitDetails && (
          <div className="space-y-6">
            {/* Visit Information */}
            <div className="bg-slate-50 p-4 rounded-lg">
              <h3 className="font-semibold mb-3">Visit Information</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <strong>Status:</strong>{' '}
                  <Badge variant="outline" className="capitalize">
                    {visitDetails.completionStatus.replace('_', ' ')}
                  </Badge>
                </div>
                <div>
                  <strong>Source:</strong>{' '}
                  <Badge variant="secondary" className="capitalize">
                    {visitDetails.submissionSource}
                  </Badge>
                </div>
                <div className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  <span>
                    <strong>Submitted:</strong>{' '}
                    {formatDistanceToNow(new Date(visitDetails.timestamp), { addSuffix: true })}
                  </span>
                </div>
                {visitDetails.latitude && visitDetails.longitude && (
                  <div className="flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    <span>
                      <strong>GPS:</strong> {visitDetails.latitude}, {visitDetails.longitude}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Media Format Summary */}
            {postingEvents.length > 0 && (
              <div>
                <h3 className="font-semibold mb-3">Campaign Tasks & Media Formats</h3>
                <div className="space-y-3">
                  {/* Group by campaign */}
                  {Object.entries(
                    postingEvents.reduce((acc, event) => {
                      const campaign = campaigns.find(c => c.id === (event as any).campaignId);
                      const format = mediaFormats.find(f => f.id === (event as any).formatId);
                      const campaignName = campaign?.name || `Campaign ${(event as any).campaignId}`;
                      
                      if (!acc[campaignName]) {
                        acc[campaignName] = [];
                      }
                      acc[campaignName].push({ ...event, format });
                      return acc;
                    }, {} as Record<string, any[]>)
                  ).map(([campaignName, events]) => (
                    <div key={campaignName} className="border rounded-lg p-4">
                      <h4 className="font-medium text-blue-700 mb-2">{campaignName}</h4>
                      <div className="grid gap-2">
                        {events.map((event) => (
                          <div key={event.eventId} className="flex justify-between items-center bg-slate-50 p-2 rounded">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="text-xs">
                                  Task {event.taskId}
                                </Badge>
                                <span className="font-medium text-sm">
                                  {event.format?.name || `Format ${(event as any).formatId}`}
                                </span>
                                <Badge variant="secondary" className="text-xs">
                                  {event.format?.type || 'unknown'}
                                </Badge>
                              </div>
                              <div className="text-xs text-gray-600 mt-1">
                                <strong>Posted:</strong> {event.postedUnits}/{(event as any).targetUnits} units
                                {event.notes && <span className="ml-2">• {event.notes}</span>}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-xs text-gray-500">
                                {formatDistanceToNow(new Date(event.timestamp), { addSuffix: true })}
                              </div>
                              {event.signedOffBy && (
                                <Badge variant="secondary" className="text-xs mt-1">
                                  {event.signOffType === 'operator' ? '✅ Operator' : '✏️ Admin'} Signed
                                </Badge>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Evidence Assets with Media Format Context */}
            {evidenceAssets.length > 0 && (
              <div>
                <h3 className="font-semibold mb-3">Evidence Assets ({evidenceAssets.length} images)</h3>
                <div className="grid grid-cols-2 gap-4">
                  {evidenceAssets.map((asset) => {
                    const relatedEvent = postingEvents.find(e => e.eventId === asset.eventId);
                    const format = mediaFormats.find(f => f.id === (relatedEvent as any)?.formatId);
                    const campaign = campaigns.find(c => c.id === (relatedEvent as any)?.campaignId);
                    
                    return (
                      <div key={asset.id} className="border rounded-lg p-3">
                        <img 
                          src={asset.secureUrl || asset.assetUrl} 
                          alt={`Evidence for ${format?.name || 'Unknown Format'}`}
                          className="w-full h-48 object-cover rounded mb-3"
                        />
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs">
                              {format?.name || `Format ${(relatedEvent as any)?.formatId}`}
                            </Badge>
                            <Badge variant="secondary" className="text-xs">
                              {format?.type || 'unknown'}
                            </Badge>
                          </div>
                          <p className="text-xs text-blue-600 font-medium">
                            {campaign?.name || `Campaign ${(relatedEvent as any)?.campaignId}`}
                          </p>
                          <p className="text-xs text-gray-500">
                            Task {relatedEvent?.taskId} • {formatDistanceToNow(new Date(asset.createdAt), { addSuffix: true })}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Current Signature */}
            {visitDetails.signatureUrl && (
              <div>
                <h3 className="font-semibold mb-3">Current Signature</h3>
                <img 
                  src={visitDetails.signatureUrl} 
                  alt="Current signature" 
                  className="max-h-32 border rounded"
                />
              </div>
            )}

            {/* Edit Form */}
            <div className="space-y-4 border-t pt-4">
              <h3 className="font-semibold">Edit Details</h3>
              
              {/* Notes */}
              <div className="space-y-2">
                <Label htmlFor="edit-notes">Notes</Label>
                <Textarea
                  id="edit-notes"
                  placeholder="Add or modify notes..."
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                />
              </div>

              {/* Discrepancy Reason */}
              <div className="space-y-2">
                <Label htmlFor="edit-discrepancy">Discrepancy Reason</Label>
                <Textarea
                  id="edit-discrepancy"
                  placeholder="Explain any discrepancies..."
                  value={formData.discrepancyReason}
                  onChange={(e) => setFormData(prev => ({ ...prev, discrepancyReason: e.target.value }))}
                  rows={2}
                />
              </div>

              {/* New Signature URL */}
              <div className="space-y-2">
                <Label htmlFor="signature-url">Update Signature URL</Label>
                <Input
                  id="signature-url"
                  placeholder="Enter new signature URL..."
                  value={formData.signatureUrl}
                  onChange={(e) => setFormData(prev => ({ ...prev, signatureUrl: e.target.value }))}
                />
              </div>

              {/* Add New Evidence */}
              <div className="space-y-2">
                <Label>Add New Evidence</Label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                  {previewUrls.length > 0 ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-3 gap-4">
                        {previewUrls.map((url, index) => (
                          <img 
                            key={index}
                            src={url} 
                            alt={`Preview ${index + 1}`} 
                            className="w-full h-32 object-cover rounded"
                          />
                        ))}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedFiles([]);
                          setPreviewUrls([]);
                        }}
                      >
                        <X className="h-4 w-4 mr-1" />
                        Clear Files
                      </Button>
                    </div>
                  ) : (
                    <div className="text-center">
                      <Camera className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                      <Input
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={handleFileSelect}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={onClose} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={saving}>
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
